/**
 * Google OAuth scope strings shared across apps and libs — the single source
 * for every scope literal used in production code (specs may keep literals:
 * a test asserting the contract value is doing its job). Grouped by API
 * family. If Gopher's DWD grant list ever changes, this file is the
 * inventory.
 */

// ── Admin SDK Directory ─────────────────────────────────────────────────────

/**
 * Read-only Directory users scope. Widely shared: the backend's DWD scope
 * list (ADMIN_DIRECTORY_SCOPES in common-backend-auth) — including the login
 * router's super-admin check — the directory API strategy's default client,
 * and the user-sync/job-step scope lists all point at this one value.
 */
export const GOOGLE_DIRECTORY_USER_READONLY_SCOPE =
  'https://www.googleapis.com/auth/admin.directory.user.readonly';

export const GOOGLE_DIRECTORY_DOMAIN_READONLY_SCOPE =
  'https://www.googleapis.com/auth/admin.directory.domain.readonly';

export const GOOGLE_DIRECTORY_ORGUNIT_SCOPE =
  'https://www.googleapis.com/auth/admin.directory.orgunit';

export const GOOGLE_DIRECTORY_GROUP_READONLY_SCOPE =
  'https://www.googleapis.com/auth/admin.directory.group.readonly';

/** Read/write — device sync also issues updates (e.g. bulk edits, OU moves). */
export const GOOGLE_DIRECTORY_DEVICE_CHROMEOS_SCOPE =
  'https://www.googleapis.com/auth/admin.directory.device.chromeos';

// ── Chrome Management ───────────────────────────────────────────────────────

export const GOOGLE_CHROME_TELEMETRY_READONLY_SCOPE =
  'https://www.googleapis.com/auth/chrome.management.telemetry.readonly';

// ── Admin SDK Reports ───────────────────────────────────────────────────────

export const GOOGLE_REPORTS_AUDIT_READONLY_SCOPE =
  'https://www.googleapis.com/auth/admin.reports.audit.readonly';

export const GOOGLE_REPORTS_USAGE_READONLY_SCOPE =
  'https://www.googleapis.com/auth/admin.reports.usage.readonly';

// ── Drive / Gmail ───────────────────────────────────────────────────────────

/** Per-file Drive access — only files the app created/was granted. */
export const GOOGLE_DRIVE_FILE_SCOPE =
  'https://www.googleapis.com/auth/drive.file';

export const GOOGLE_GMAIL_SETTINGS_BASIC_SCOPE =
  'https://www.googleapis.com/auth/gmail.settings.basic';

// NOTE: the Cloud SQL IAM scope (sqlservice.admin) deliberately lives in
// cloud-sql-connector-utils — it's GCP infra, not a Workspace/DWD grant, and
// that low-level lib stays free of this package's dependency chain.

// ── DWD grant list ──────────────────────────────────────────────────────────

/** One entry in {@link GOPHER_DWD_GRANTS}: the scope plus how Step 3 describes it. */
export interface GopherDwdGrant {
  scope: string;
  name: string;
  purpose: string;
}

/**
 * The exact Workspace scopes a customer's admin must grant Gopher via
 * domain-wide delegation for MVP sync (onboarding Step 3, US-5/6). This is the
 * single source the wizard's "Authorize" deep link pre-fills (clientScopeToAdd)
 * so what the admin grants always matches what the sync pipeline uses.
 *
 * Scoped to the current device product: the directory reads + org-unit/device
 * read-writes and telemetry that the sync job-steps and common-backend-apis
 * device/user/group/orgunit services actually call, plus `drive.file` for
 * Google Sheets export sharing and Drive-picker imports. Deliberately excluded
 * until their features are in scope: `gmail.settings.basic` (gmail) and
 * `admin.reports.audit` (sessions sync is gopher-buddy/G4C, not the spog
 * pipeline). Add here if the grant list grows — the entry's `name` and
 * `purpose` are what Step 3's scope table shows the admin.
 *
 * Adding a scope here also tightens verification: `DwdCheckService.checkDwd`
 * mints one token for the whole list, so a tenant that hasn't granted the new
 * scope fails Step 3 until it re-authorizes through the deep link.
 */
export const GOPHER_DWD_GRANTS: readonly GopherDwdGrant[] = [
  {
    scope: GOOGLE_DIRECTORY_USER_READONLY_SCOPE,
    name: 'Users',
    purpose: 'Read your users’ directory profiles for search and account mapping.',
  },
  {
    scope: GOOGLE_DIRECTORY_GROUP_READONLY_SCOPE,
    name: 'Groups',
    purpose: 'Read your Google Groups and their memberships.',
  },
  {
    // Read/write — OU moves.
    scope: GOOGLE_DIRECTORY_ORGUNIT_SCOPE,
    name: 'Organizational Units',
    purpose:
      'Read and update your organizational unit (OU) tree for scoped management.',
  },
  {
    // Read/write — bulk edits, OU moves.
    scope: GOOGLE_DIRECTORY_DEVICE_CHROMEOS_SCOPE,
    name: 'ChromeOS Devices',
    purpose: 'Retrieve, update, and deprovision ChromeOS devices.',
  },
  {
    scope: GOOGLE_CHROME_TELEMETRY_READONLY_SCOPE,
    name: 'ChromeOS Telemetry',
    purpose: 'Read ChromeOS device telemetry such as hardware and usage.',
  },
  {
    scope: GOOGLE_DRIVE_FILE_SCOPE,
    name: 'Google Drive (app files only)',
    purpose:
      'Create and share the Google Sheets exports you request, and open files you pick for imports.',
  },
];

/** Just the scope strings from {@link GOPHER_DWD_GRANTS}, in order — what the
 *  deep link pre-fills and what the DWD check requests. */
export const GOPHER_DWD_GRANT_SCOPES: readonly string[] = GOPHER_DWD_GRANTS.map(
  (grant) => grant.scope
);

/** The Google Admin Console domain-wide-delegation "add client" view. The
 *  client id + scopes are appended as query params to pre-fill it. */
export const GAC_DWD_DELEGATION_URL =
  'https://admin.google.com/ac/owl/domainwidedelegation';

/** Shape of the open `GET /authentication/dwd-client-id` response — Gopher's
 *  DWD service-account OAuth client id, read live from the server secret so it
 *  never drifts. Non-secret (admins paste it into Google's console). */
export interface DwdClientIdResponse {
  clientId: string;
}

/**
 * Build the pre-filled Google Admin Console DWD deep link, shared so the
 * onboarding wizard (Step 3) and the INTEGRATION_BROKEN block screen construct
 * it identically instead of each hand-rolling the URL.
 *
 * With no `clientId` it returns the bare console URL — still opens the right
 * page, just not pre-filled — so a caller that fetches the id asynchronously
 * always has a working link to render while it loads or if the fetch fails.
 */
export function buildDwdDelegationLink(
  clientId: string | null | undefined,
  scopes: readonly string[] = GOPHER_DWD_GRANT_SCOPES
): string {
  if (!clientId) {
    return GAC_DWD_DELEGATION_URL;
  }
  const params = new URLSearchParams({
    overwriteClientId: 'true',
    clientIdToAdd: clientId,
    clientScopeToAdd: scopes.join(','),
  });
  return `${GAC_DWD_DELEGATION_URL}?${params.toString()}`;
}
